import { ChangeEvent, useRef, useState } from 'react';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFile } from '@components/icons/file';
import { IconFiles } from '@components/icons/files';
import { IconTrash } from '@components/icons/trash';
import { StandaloneMenuItem } from '@components/menu';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { formatNumber } from '@utils/numbers';

import { EditableCellDropzone } from './editable-cell-dropzone';
import { EditableCellPopover } from './editable-cell-popover';
import { getFileCellSummary, getIsFileDropzoneVisible } from './file-value';
import { editableCellLocale } from './locale';
import { EditableCellEditorProps, FileCellEntry } from './types';
import { useEditableCellTrigger } from './use-editable-cell-trigger';

import { FileEditValue } from '../types';

type EditableFileCellProps = EditableCellEditorProps<Array<FileCellEntry> | undefined>;

export const EditableFileCell = ({ column, row, value }: EditableFileCellProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const getTrigger = useEditableCellTrigger();
  const showFilePreviewModal = usePreviewFileModal();
  const inputRef = useRef<HTMLInputElement>(null);
  const isMulti = column.type === 'file_multi';

  const [addedFiles, setAddedFiles] = useState<Array<File>>([]);
  const [removedFileShortIds, setRemovedFileShortIds] = useState<Array<string>>([]);

  const currentFiles: Array<FileCellEntry> = Array.isArray(value) ? value : [];
  const remainingFiles = currentFiles.filter(
    (file) => !removedFileShortIds.includes(String(file.id)),
  );

  const summary = getFileCellSummary(currentFiles);
  const isDropzoneVisible = getIsFileDropzoneVisible({
    isMulti,
    fileCount: remainingFiles.length + addedFiles.length,
  });
  const hasFileRows = remainingFiles.length > 0 || addedFiles.length > 0;

  // A file column reads as an icon and a line of text, the way every other
  // column reads - the file's name when there is one, a count when there are
  // several. renderValue only runs for a non-empty cell, so the count label
  // covers every case the name doesn't.
  const trigger = getTrigger(
    value,
    column,
    row,
    () => (
      <TriggerRow>
        {summary.kind === 'many' ? (
          <IconFiles color="buttonIconPositive" />
        ) : (
          <IconFile color="buttonIconPositive" />
        )}
        <TriggerLabel>
          <Text
            font="regular14"
            color="textSecondary"
            text={
              summary.kind === 'single'
                ? summary.name
                : t('fileCount', { count: formatNumber(currentFiles.length) })
            }
          />
        </TriggerLabel>
      </TriggerRow>
    ),
    () => (
      <TriggerRow>
        <IconAddPlus color="buttonIconPositive" />
        <TriggerLabel>
          <Text font="regular14" color="textHelper" text={isMulti ? t('noFiles') : t('noFile')} />
        </TriggerLabel>
      </TriggerRow>
    ),
  );

  const isSaveDisabled = addedFiles.length === 0 && removedFileShortIds.length === 0;

  const addFiles = (files: Array<File>) => {
    if (files.length === 0) {
      return;
    }

    setAddedFiles((current) => (isMulti ? current.concat(files) : [files[0]]));
  };

  const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    addFiles(files);
  };

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      isSaveDisabled={isSaveDisabled}
      isSaveOnEnter={false}
      onOpen={() => {
        setAddedFiles([]);
        setRemovedFileShortIds([]);
      }}
      onSave={async () => {
        const editValue: FileEditValue = { addedFiles, removedFileShortIds };
        await column.onEdit?.(row, editValue);
      }}
    >
      <FileEditorBody>
        <Stack space={4}>
          {hasFileRows && (
            <Stack>
              {remainingFiles.map((file) => (
                <FileRow key={file.id || file.name}>
                  <FileRowLabel>
                    <StandaloneMenuItem
                      label={file.name}
                      icon={<IconFile />}
                      onSelect={() =>
                        showFilePreviewModal({
                          formFile: file,
                          filesList: remainingFiles,
                        })
                      }
                    />
                  </FileRowLabel>
                  {file.id !== '' && (
                    <IconButton
                      variant="ghost-danger"
                      icon={<IconTrash />}
                      aria-label={t('removeFile', { name: file.name })}
                      onClick={() =>
                        setRemovedFileShortIds((current) => current.concat(String(file.id)))
                      }
                    />
                  )}
                </FileRow>
              ))}
              {/* A picked file has no stored source yet, so its row is static -
                  there is nothing to preview until the save lands. */}
              {addedFiles.map((file, index) => (
                <FileRow key={`${file.name}-${index}`}>
                  <FileRowLabel>
                    <StandaloneMenuItem label={file.name} icon={<IconFile />} />
                  </FileRowLabel>
                  <IconButton
                    variant="ghost-danger"
                    icon={<IconTrash />}
                    aria-label={t('removeFile', { name: file.name })}
                    onClick={() =>
                      setAddedFiles((current) =>
                        current.filter((_, fileIndex) => fileIndex !== index),
                      )
                    }
                  />
                </FileRow>
              ))}
            </Stack>
          )}
          {isDropzoneVisible && (
            <>
              {hasFileRows && <Divider />}
              <EditableCellDropzone
                isMulti={isMulti}
                onFilesPicked={addFiles}
                onBrowse={() => inputRef.current?.click()}
              />
              <VisuallyHidden>
                <input ref={inputRef} type="file" multiple={isMulti} onChange={handleFilesPicked} />
              </VisuallyHidden>
            </>
          )}
        </Stack>
      </FileEditorBody>
    </EditableCellPopover>
  );
};

// The designs draw the file panel at 420px, which is 388px of content next to
// the panel's own padding. Holding one width across every state keeps the
// panel from resizing as files are added and removed.
const FileEditorBody = styled.div({ width: pxToRem(388) });

const FileRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
});

const FileRowLabel = styled.div({
  flex: '1 1 auto',
  minWidth: 0,
});

const Divider = styled.div({
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
});

const TriggerRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(4),
  minWidth: 0,
});

const TriggerLabel = styled.span({
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});
